/**
 * User Growth Slide
 * Stacked area chart showing user growth by distribution channel over time
 * with channel descriptions alongside
 */

import React from 'react';
import styled from 'styled-components';
import { colors } from '@config/colors';
import { typography } from '@config/typography';
import { spacing, borderRadius } from '@config/spacing';
import { media } from '@config/breakpoints';
import {
  SlideBase,
  SlideContent,
  SlideHeader,
  SlideAccentGlow,
} from '@components/press/pitch-deck/slide-primitives';

/* -------------------------------------------------- */
/* Chart data                                         */
/* -------------------------------------------------- */

interface DataPoint {
  direct: number;
  community: number;
  aio: number;
  momfluencer: number;
  paid: number;
}

const DATA: DataPoint[] = [
  { direct: 2,  community: 0,  aio: 0,  momfluencer: 0,  paid: 0 },
  { direct: 5,  community: 0,  aio: 0,  momfluencer: 0,  paid: 0 },
  { direct: 8,  community: 2,  aio: 0,  momfluencer: 0,  paid: 0 },
  { direct: 12, community: 5,  aio: 1,  momfluencer: 0,  paid: 0 },
  { direct: 15, community: 10, aio: 3,  momfluencer: 2,  paid: 0 },
  { direct: 18, community: 16, aio: 6,  momfluencer: 5,  paid: 0 },
  { direct: 20, community: 22, aio: 10, momfluencer: 10, paid: 4 },
  { direct: 22, community: 28, aio: 15, momfluencer: 16, paid: 12 },
];

interface ChannelDef {
  key: keyof DataPoint;
  label: string;
  desc: string;
  color: string;
}

const CHANNELS: ChannelDef[] = [
  {
    key: 'direct',
    label: 'Direct Referrals',
    desc: 'Immediate household networks of spouses, care providers from seed connections',
    color: colors.accent.red.base,
  },
  {
    key: 'community',
    label: 'Community',
    desc: 'Play date groups, school calendars, custom household solves for key areas like the Bay Area, general community awareness',
    color: colors.accent.orange.base,
  },
  {
    key: 'aio',
    label: 'AIO',
    desc: 'Login and special tool access for personalized household models',
    color: colors.accent.yellow.base,
  },
  {
    key: 'momfluencer',
    label: 'Momfluencer',
    desc: 'Brand partnerships with mom-focused content creators driving awareness and sign-ups',
    color: colors.accent.green.base,
  },
  {
    key: 'paid',
    label: 'Paid',
    desc: 'Traditional marketing channels',
    color: colors.accent.blue.base,
  },
];

/* -------------------------------------------------- */
/* SVG chart helpers                                  */
/* -------------------------------------------------- */

const CHART_W = 420;
const CHART_H = 300;
const PAD_L = 24;
const PAD_R = 24;
const PAD_T = 16;
const PAD_B = 32;
const PLOT_W = CHART_W - PAD_L - PAD_R;
const PLOT_H = CHART_H - PAD_T - PAD_B;

const maxTotal = Math.max(...DATA.map(d => d.direct + d.community + d.aio + d.momfluencer + d.paid));

function xPos(i: number) {
  return PAD_L + (i / (DATA.length - 1)) * PLOT_W;
}

function yPos(val: number) {
  return PAD_T + PLOT_H - (val / maxTotal) * PLOT_H;
}

function areaPath(cumBottom: number[], cumTop: number[]): string {
  const fwd = cumTop.map((v, i) => `${i === 0 ? 'M' : 'L'}${xPos(i)},${yPos(v)}`).join(' ');
  const rev = [...cumBottom].reverse().map((v, i) => {
    const idx = cumBottom.length - 1 - i;
    return `L${xPos(idx)},${yPos(v)}`;
  }).join(' ');
  return `${fwd} ${rev} Z`;
}

/* -------------------------------------------------- */
/* Local styled components                            */
/* -------------------------------------------------- */

const SlideLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xl};
  width: 100%;
  max-width: 1080px;

  ${media.md} {
    flex-direction: row;
    align-items: flex-start;
  }
`;

const ChartWrapper = styled.div`
  flex: 1;
  min-width: 0;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: ${borderRadius.xl};
  padding: ${spacing.lg};
  box-shadow: 0 2px 8px rgba(51, 65, 85, 0.06);

  ${media.md} {
    padding: ${spacing.xl};
  }
`;

const ChartSvg = styled.svg`
  width: 100%;
  height: auto;
  display: block;
`;

const ChannelList = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
  width: 100%;

  ${media.md} {
    width: 340px;
  }
`;

const ChannelItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${spacing.sm};
`;

const ChannelSwatch = styled.div<{ $color: string }>`
  width: 12px;
  height: 12px;
  border-radius: ${borderRadius.sm};
  background: ${(p) => p.$color};
  flex-shrink: 0;
  margin-top: 3px;
`;

const ChannelText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const ChannelName = styled.span`
  font-family: ${typography.fontFamily.heading};
  font-size: clamp(${typography.fontSize.xs}, 1.1vw, ${typography.fontSize.sm});
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.highlight.dark};
`;

const ChannelDesc = styled.span`
  font-family: ${typography.fontFamily.body};
  font-size: clamp(${typography.fontSize.xxs}, 0.9vw, ${typography.fontSize.xs});
  color: ${colors.highlight.light};
  line-height: ${typography.lineHeight.relaxed};
`;

/* -------------------------------------------------- */
/* Build stacked layers                               */
/* -------------------------------------------------- */

const STACK_ORDER: (keyof DataPoint)[] = ['direct', 'community', 'aio', 'momfluencer', 'paid'];

function buildLayers() {
  const layers: { key: keyof DataPoint; path: string; color: string }[] = [];
  const cumBottoms = DATA.map(() => 0);

  for (const key of STACK_ORDER) {
    const cumTops = DATA.map((d, i) => cumBottoms[i] + d[key]);
    const channelMeta = CHANNELS.find(c => c.key === key)!;
    layers.push({ key, path: areaPath(cumBottoms, cumTops), color: channelMeta.color });
    cumTops.forEach((v, i) => { cumBottoms[i] = v; });
  }

  return layers;
}

const layers = buildLayers();

/* X-axis year positions — inset from edges */
const YEAR_POSITIONS = [
  { label: '2026', idx: 0.5 },
  { label: '2027', idx: (DATA.length - 1) / 2 },
  { label: '2028', idx: DATA.length - 1.5 },
];

/* -------------------------------------------------- */
/* Component                                          */
/* -------------------------------------------------- */

export const GrowthSlide: React.FC = () => (
  <SlideBase>
    <SlideAccentGlow />
    <SlideContent>
      <SlideHeader
        title="User Growth"
        subtitle="User acquisition by distribution channel over time"
      />

      <SlideLayout>
        <ChartWrapper>
          <ChartSvg viewBox={`0 0 ${CHART_W} ${CHART_H}`} preserveAspectRatio="xMidYMid meet">
            {/* Baseline */}
            <line
              x1={PAD_L} x2={CHART_W - PAD_R}
              y1={yPos(0)} y2={yPos(0)}
              stroke={colors.neutral.light}
              strokeWidth={1}
            />

            {/* Stacked area layers */}
            {layers.map((layer) => (
              <path key={layer.key} d={layer.path} fill={layer.color} opacity={0.85} />
            ))}

            {/* X-axis year labels */}
            {YEAR_POSITIONS.map((yp) => (
              <text
                key={yp.label}
                x={xPos(yp.idx)}
                y={CHART_H - 6}
                textAnchor="middle"
                fill={colors.highlight.light}
                fontSize="13"
                fontWeight="500"
                fontFamily={typography.fontFamily.body}
              >
                {yp.label}
              </text>
            ))}
          </ChartSvg>
        </ChartWrapper>

        <ChannelList>
          {CHANNELS.map((ch) => (
            <ChannelItem key={ch.key}>
              <ChannelSwatch $color={ch.color} />
              <ChannelText>
                <ChannelName>{ch.label}</ChannelName>
                <ChannelDesc>{ch.desc}</ChannelDesc>
              </ChannelText>
            </ChannelItem>
          ))}
        </ChannelList>
      </SlideLayout>
    </SlideContent>
  </SlideBase>
);
